import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, InputLabel, Select, MenuItem, Box
} from '@mui/material';
import { database as db } from './../firebaseConfig';
import { fetchConfirmations } from '../adminData';
import {
  getConfirmationName,
  confirmationUnit,
  confirmationAmount,
  confirmationRound,
} from '../bookings';

function downloadCSV(rows, sortedDates, scopeLabel) {
  const headers = ['Name', 'Building + Unit', 'Round', ...sortedDates, 'Total'];
  const csvRows = [headers.join(',')];

  rows.forEach(row => {
    const line = [
      row.name,
      row.buildingUnit,
      row.round,
      ...sortedDates.map(d => row.sets[d] || 0),
      row.total.toFixed(2)
    ];
    csvRows.push(line.join(','));
  });

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `paid-confirmations-${scopeLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PaidSummaryDialog({ open, onClose }) {
  const [paid, setPaid] = useState([]);
  const [selectedRound, setSelectedRound] = useState('all');

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const all = await fetchConfirmations(db);
        setPaid(all.filter(conf => conf.status === 'PAID'));
      } catch (error) {
        console.error('Error loading confirmations:', error);
        setPaid([]);
      }
    };

    load();
  }, [open]);

  const uniqueRounds = useMemo(
    () => Array.from(new Set(paid.map(confirmationRound))).sort((a, b) => a - b),
    [paid],
  );

  const { rows, sortedDates, totals } = useMemo(() => {
    const scoped = paid.filter(
      conf => selectedRound === 'all' || confirmationRound(conf) === selectedRound,
    );

    const allDatesSet = new Set();
    const data = scoped.map(conf => {
      const cartDates = conf.cart?.dates || [];
      cartDates.forEach(d => allDatesSet.add(d));
      const sets = {};
      cartDates.forEach((date, i) => {
        sets[date] = conf.cart?.sets?.[i] || 0;
      });
      return {
        name: getConfirmationName(conf),
        buildingUnit: confirmationUnit(conf),
        round: confirmationRound(conf),
        sets,
        total: parseFloat(confirmationAmount(conf) || 0),
      };
    });

    const dateArray = Array.from(allDatesSet).sort();
    const columnTotals = { total: 0 };
    dateArray.forEach(d => (columnTotals[d] = 0));
    data.forEach(row => {
      dateArray.forEach(d => {
        columnTotals[d] += row.sets[d] || 0;
      });
      columnTotals.total += row.total;
    });

    return { rows: data, sortedDates: dateArray, totals: columnTotals };
  }, [paid, selectedRound]);

  const scopeLabel =
    selectedRound === 'all' ? 'all-rounds' : `round-${selectedRound}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Summary of PAID Confirmations
        {selectedRound !== 'all' && ` — Round ${selectedRound}`}
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Booking Round</InputLabel>
            <Select
              value={selectedRound}
              label="Booking Round"
              onChange={(e) => setSelectedRound(e.target.value)}
            >
              <MenuItem value="all">All Rounds</MenuItem>
              {uniqueRounds.map(round => (
                <MenuItem key={round} value={round}>Round {round}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Building + Unit</strong></TableCell>
              <TableCell align="right"><strong>Rd</strong></TableCell>
              {sortedDates.map(date => (
                <TableCell key={date} align="right"><strong>{date}</strong></TableCell>
              ))}
              <TableCell align="right"><strong>Total Paid ($)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.buildingUnit}</TableCell>
                <TableCell align="right">{row.round}</TableCell>
                {sortedDates.map(date => (
                  <TableCell key={date} align="right">{row.sets[date] || 0}</TableCell>
                ))}
                <TableCell align="right">${row.total?.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell />
              <TableCell />
              {sortedDates.map(date => (
                <TableCell key={date} align="right">
                  <strong>{totals[date]}</strong>
                </TableCell>
              ))}
              <TableCell align="right"><strong>${totals.total?.toFixed(2)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => downloadCSV(rows, sortedDates, scopeLabel)}>
          Download CSV
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
